// Helpers for the document-template preview iframe.
//
// The server returns a complete, print-ready HTML document (DocumentGeneration::
// TemplateRenderer) whose @page rules only paginate in *print* media — in a plain
// screen iframe it renders as one long strip. Injecting the paged.js polyfill makes
// those same rules (page size/margins, our `page_break` divs, the optional
// page-number footer) render as real page boxes on screen, so the preview matches
// the PDF the WeasyPrint pipeline produces from the identical CSS.

// The polyfill is served as a static asset (public/pagedjs/) rather than bundled:
// pagedjs's `exports` map doesn't expose its dist subpath, so Vite can't import it.
// Loading it via <script src> from the app origin also keeps the ~500 KB out of the
// JS bundle and lets the iframe run it in its own document context (it auto-starts).
export const PAGEDJS_POLYFILL_URL = '/pagedjs/paged.polyfill.min.js';

// Screen-only styling for the paged.js page boxes: white sheets on the grey canvas.
// Scoped to @media screen and to paged.js's own .pagedjs_* classes, so the
// print/PDF output is untouched.
export const PREVIEW_SCREEN_CSS =
  '@media screen{html,body{background:#f1f3f5;margin:0}' +
  '.pagedjs_pages{display:flex;flex-direction:column;align-items:center;gap:16px;padding:16px}' +
  '.pagedjs_page{background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.08)}}';

/**
 * Inject the screen CSS and the (self-running) paged.js polyfill into a complete
 * HTML document string, returning HTML suitable for an iframe `srcdoc`.
 *
 * @param {string} html - the server-rendered print-ready document.
 * @returns {string}
 */
export function injectPagination(html) {
  const style = `<style>${PREVIEW_SCREEN_CSS}</style>`;
  // Split the closing-script token so a bundler/SFC parser can't end a block early.
  const script = `<script src="${PAGEDJS_POLYFILL_URL}"><\/script>`;
  const withStyle = html.includes('</head>')
    ? html.replace('</head>', `${style}</head>`)
    : `${style}${html}`;
  return withStyle.includes('</body>')
    ? withStyle.replace('</body>', `${script}</body>`)
    : `${withStyle}${script}`;
}

export default injectPagination;
